<template>
    <v-card>
        <v-form
                ref="user-form"
                lazy-validation
        >
            <v-card-title>
                <v-icon left>mdi-account</v-icon>
                {{ $t('dialog.form.user' ) }}
            </v-card-title>
            <v-card-text>
                <v-row>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="user.name"
                            :label="$t('form.user.name.label')"
                            :error-messages="nameErrors"
                            :counter="20"
                            required
                            @blur="$v.user.name.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="user.firstname"
                            :label="$t('form.user.firstname.label')"
                            :error-messages="firstnameErrors"
                            :counter="20"
                            required
                            @blur="$v.user.firstname.$touch()"
                        ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="user.company"
                            :label="$t('form.user.company.label')"
                            :error-messages="companyErrors"
                            :counter="20"
                            required
                            @blur="$v.user.company.$touch()"
                        ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="user.company_role"
                            :label="$t('form.user.company_role.label')"
                            :error-messages="companyRoleErrors"
                            :counter="20"
                            required
                            @blur="$v.user.company_role.$touch()"
                        ></v-text-field>
                    </v-col>

                    <v-col cols="12">
                        <v-file-input
                            v-model="avatar"
                            :rules="avatarRules"
                            accept="image/*"
                            :label="$t('form.user.avatar.label')"
                            show-size
                            prepend-icon="mdi-camera"
                        ></v-file-input>
                    </v-col>

                    <v-col cols="12">
                        <v-text-field
                                v-model="user.email"
                                :label="$t('form.user.email.label')"
                                :error-messages="emailErrors"
                                required
                                @blur="$v.user.email.$touch()"
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                        <v-text-field
                                v-model="user.repeatEmail"
                                :label="$t('form.user.email-repeat.label')"
                                :error-messages="repeatEmailErrors"
                                required
                                @blur="$v.user.repeatEmail.$touch()"
                        ></v-text-field>
                    </v-col>

                    <v-col cols="12">
                        <v-select
                            v-model="user.locale"
                            :items="nativeLocals"
                            item-text="native"
                            item-value="iso"
                        ></v-select>
                    </v-col>
                </v-row>
                <br>
                <v-divider></v-divider>
            </v-card-text>

            <v-card-actions>
                <v-btn
                    text
                    @click="close"
                >
                    {{ $t('controls.cancel') }}
                </v-btn>

                <v-spacer></v-spacer>

                <v-btn
                    dark
                    @click="save"
                >
                    {{ $t('controls.save') }}
                </v-btn>
            </v-card-actions>
        </v-form>
    </v-card>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { maxLength, minLength, required, email, sameAs } from 'vuelidate/lib/validators'
  import { validationMixin } from 'vuelidate'
  import { localisation } from '../../settings/settings.js'

  export default {
    name: 'UserForm',
    mixins: [validationMixin],

    validations: {
      user: {
        name: {
          required,
          minLength: minLength(4),
          maxLength: maxLength(20)
        },
        firstname: {
          required,
          minLength: minLength(4),
          maxLength: maxLength(20)
        },
        company: {
          minLength: minLength(4),
          maxLength: maxLength(20)
        },
        company_role: {
          minLength: minLength(4),
          maxLength: maxLength(20)
        },
        email: {
          required,
          email
        },
        repeatEmail: {
          required,
          email,
          sameAsEmail: sameAs('email')
        }
      }
    },

    data: function () {
      return {
        ...localisation,
        avatarRules: [
          value => !value || value.size < 500000 || this.$t('form.user.avatar.file_size'),
        ],
        avatar: null,
        user: {
          locale: this.defaultLocale,
          name: '',
          firstname: '',
          company: '',
          company_role: '',
          email: '',
          repeatEmail: ''
        }
      }
    },

    computed: {
      ...mapGetters({
        userInfo: 'user/userInfo'
      }),

      nameErrors () {
        const errors = []
        if (!this.$v.user.name.$dirty) return errors
        !this.$v.user.name.minLength && errors.push(this.$t('form.user.name.min-length'))
        !this.$v.user.name.maxLength && errors.push(this.$t('form.user.name.max-length'))
        !this.$v.user.name.required && errors.push(this.$t('form.user.name.required'))
        return errors
      },

      firstnameErrors () {
        const errors = []
        if (!this.$v.user.firstname.$dirty) return errors
        !this.$v.user.firstname.minLength && errors.push(this.$t('form.user.firstname.min-length'))
        !this.$v.user.firstname.maxLength && errors.push(this.$t('form.user.firstname.max-length'))
        !this.$v.user.firstname.required && errors.push(this.$t('form.user.firstname.required'))
        return errors
      },

      companyErrors () {
        const errors = []
        if (!this.$v.user.company.$dirty) return errors
        !this.$v.user.company.minLength && errors.push(this.$t('form.user.company.min-length'))
        !this.$v.user.company.maxLength && errors.push(this.$t('form.user.company.max-length'))
        return errors
      },

      companyRoleErrors () {
        const errors = []
        if (!this.$v.user.company_role.$dirty) return errors
        !this.$v.user.company_role.minLength && errors.push(this.$t('form.user.company_role.min-length'))
        !this.$v.user.company_role.maxLength && errors.push(this.$t('form.user.company_role.max-length'))
        return errors
      },

      emailErrors () {
        const errors = []
        if (!this.$v.user.email.$dirty) return errors
        !this.$v.user.email.required && errors.push(this.$t('form.user.email.required'))
        !this.$v.user.email.email && errors.push(this.$t('form.user.email.not-valid'))
        return errors
      },

      repeatEmailErrors () {
        const errors = []
        if (!this.$v.user.repeatEmail.$dirty) return errors
        !this.$v.user.repeatEmail.required && errors.push(this.$t('form.user.email-repeat.required'))
        !this.$v.user.repeatEmail.email && errors.push(this.$t('form.user.email-repeat.not-valid'))
        !this.$v.user.repeatEmail.sameAsEmail && errors.push(this.$t('form.user.email-repeat.same-as'))
        return errors
      },

      nativeLocals() {
        let locals = [];

        for (let i in this.validLocals)
        {
          locals.push({ iso: this.validLocals[i], native: this.$t('language.' + this.validLocals[i])})
        }

        return locals;
      }
    },

    created () {
      this.user = Object.assign({}, this.userInfo)
    },

    methods: {
      save () {
        this.$v.$touch()
        if (!this.$v.$invalid) {

          let formData = new FormData()

          formData.append('locale', this.user.locale);
          formData.append('name', this.user.name);
          formData.append('firstname', this.user.firstname);
          formData.append('company', this.user.company);
          formData.append('company_role', this.user.company_role);
          formData.append('email', this.user.email);
          formData.append('repeatEmail', this.user.repeatEmail);

          // only append avatar to form data if it's not empty
          if(this.avatar)
            formData.append('avatar', this.avatar)

          this.$store.dispatch('user/updateUserInfo', formData)
            .then(() => {
              this.$root.$i18n.locale = this.user.locale;

              this.$store.commit('notification/newNotification', {
                text: this.$t('form.user.updated')
              })

              this.$emit('settingsIsProcessed')
              this.reset();
            })
        }
      },

      close () {
        this.$emit('settingsIsCanceled');
        this.reset();
      },

      reset() {
        this.user = Object.assign({}, this.userInfo);
        this.avatar = null;
        this.$v.$reset()
      },
    }

  }
</script>

<style scoped>

</style>
